import React, { useEffect, useMemo } from 'react';
import { InputAddon } from '../../../../../../../components/InputAddon';
import { InputCurrency } from '../../../../../../../components/InputCurrency';
import { InputGroup } from '../../../../../../../components/InputGroup';
import { useOfferFormContext } from '../../context';
import type { OfferFormState } from '../../types/offer.types';
import styles from './OfferTypeForm.module.scss';

type OfferTypeFormProps = {
  value?: OfferFormState;
  onChange?: (data: OfferFormState) => void;
};

const Label = ({ children }: { children: React.ReactNode }) => {
  return <p className={styles.label}>{children}</p>;
};

const Row = ({ children }: { children: React.ReactNode }) => {
  return <div className={styles.row}>{children}</div>;
};

const Col = ({ children }: { children: React.ReactNode }) => {
  return <div className={styles.col}>{children}</div>;
};

export const OfferTypeForm = ({ value, onChange }: OfferTypeFormProps) => {
  const { offerType: offerTypeSelected, offerForm, setOfferForm } = useOfferFormContext();

  const initialState: OfferFormState = useMemo(
    () => ({
      offer_type: offerTypeSelected,
      offer_price: null,
      percentage_discount: null,
      from: null,
      to: null,
      buy_quantity: null,
      pay_quantity: null,
      unit_buy: null,
      minimum_quantity: null,
      price_per_unit: null,
    }),
    [offerTypeSelected],
  );

  useEffect(() => {
    const base = value ? { ...value } : {};
    setOfferForm({ ...initialState, ...base });
  }, []);

  useEffect(() => {
    if (offerTypeSelected) {
      setOfferForm((prev) => ({ ...prev, offer_type: offerTypeSelected }));
    }
  }, [offerTypeSelected]);

  useEffect(() => {
    onChange?.(offerForm);
  }, [offerForm, onChange]);

  if (!offerTypeSelected) return null;

  const handleField = (key: keyof OfferFormState, v: number | null) => {
    setOfferForm((prev) => ({ ...prev, [key]: v }));
  };

  if (offerTypeSelected === 'SIMPLE_PRICE') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>Preço oferta</Label>
            <InputGroup>
              <InputAddon>R$</InputAddon>
              <InputCurrency
                value={offerForm.offer_price || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="2"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('offer_price', v)}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  if (offerTypeSelected === 'SIMPLE_PERCENTAGE') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>% Desconto</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.percentage_discount || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="1"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('percentage_discount', v)}
              />
              <InputAddon>%</InputAddon>
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  if (offerTypeSelected === 'FROM_TO') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>De</Label>
            <InputGroup>
              <InputAddon>R$</InputAddon>
              <InputCurrency
                value={offerForm.from || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="2"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('from', v)}
              />
            </InputGroup>
          </Col>
          <Col>
            <Label>Por</Label>
            <InputGroup>
              <InputAddon>R$</InputAddon>
              <InputCurrency
                value={offerForm.to || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="2"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('to', v)}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  if (offerTypeSelected === 'BUY_PAY_QUANTITY') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>Leve</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.buy_quantity || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('buy_quantity', v)}
              />
              <InputAddon>un</InputAddon>
            </InputGroup>
          </Col>
          <Col>
            <Label>Pague</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.pay_quantity || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('pay_quantity', v)}
              />
              <InputAddon>un</InputAddon>
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  if (offerTypeSelected === 'BUY_GET_DISCOUNT') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>Leve</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.buy_quantity || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('buy_quantity', v)}
              />
              <InputAddon>un</InputAddon>
            </InputGroup>
          </Col>
          <Col>
            <Label>% Desconto</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.percentage_discount || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="1"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('percentage_discount', v)}
              />
              <InputAddon>%</InputAddon>
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  if (offerTypeSelected === 'UNIT_DISCOUNT') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>% Desconto</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.percentage_discount || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="1"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('percentage_discount', v)}
              />
              <InputAddon>%</InputAddon>
            </InputGroup>
          </Col>
          <Col>
            <Label>Na</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.unit_buy || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('unit_buy', v)}
              />
              <InputAddon style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>ª</span>
                <span>un</span>
              </InputAddon>
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  if (offerTypeSelected === 'BULK_PERCENTAGE') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>A partir de</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.minimum_quantity || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('minimum_quantity', v)}
              />
              <InputAddon>un</InputAddon>
            </InputGroup>
          </Col>
          <Col>
            <Label>% Desconto</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.percentage_discount || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="1"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('percentage_discount', v)}
              />
              <InputAddon>%</InputAddon>
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  if (offerTypeSelected === 'BULK_PRICE') {
    return (
      <div className={styles.container}>
        <Row>
          <Col>
            <Label>A partir de</Label>
            <InputGroup>
              <InputCurrency
                value={offerForm.minimum_quantity || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('minimum_quantity', v)}
              />
              <InputAddon>un</InputAddon>
            </InputGroup>
          </Col>
          <Col>
            <Label>Pague</Label>
            <InputGroup>
              <InputAddon>R$</InputAddon>
              <InputCurrency
                value={offerForm.price_per_unit || null}
                decimalSeparator=","
                thousandSeparator="."
                precision="2"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, v) => handleField('price_per_unit', v)}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>
    );
  }

  return null;
};

export default OfferTypeForm;
